import type { StaffImportCandidate } from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";

import { ItemStatusBadge } from "../../../catalog/components/ItemStatusBadge";
import { groupHeading, hintText, listRow, plainList } from "../../../catalog/components/styles";
import { cn } from "../../../ui/cn";
import { DataTable } from "../../../ui/data-table";
import { Notice } from "../../../ui/notice";
import { emptyValue, rawText } from "../../components/styles";
import { percent, targetFieldLabel } from "../format";
import { matchBasisLabels, matchStrengthLabels } from "../labels";

type Change = StaffImportCandidate["changes"][number];

const column = createColumnHelper<Change>();

const changeColumns = [
  column.accessor((change) => targetFieldLabel(change.fieldCode), {
    id: "field",
    header: "Field",
    meta: { rowHeader: true },
  }),
  column.display({
    id: "now",
    header: "Item now",
    meta: { className: rawText },
    cell: (info) => info.row.original.current ?? <span className={emptyValue}>(empty)</span>,
  }),
  column.display({
    id: "proposed",
    header: "From the file",
    meta: { className: rawText },
    cell: (info) => info.row.original.proposed ?? <span className={emptyValue}>(empty)</span>,
  }),
  column.display({
    id: "effect",
    header: "Effect",
    cell: (info) => {
      const change = info.row.original;
      return change.destructive ? (
        <strong>
          <span aria-hidden="true">⚠ </span>Replaces a value
        </strong>
      ) : change.current === null ? (
        "Fills an empty value"
      ) : (
        "Adds to the item, replaces nothing"
      );
    },
  }),
];

/**
 * The existing items a row may match, with the changes an update would make. A change that replaces a
 * value the item already has is marked with a glyph and a word, not by colour alone. Names and values are
 * untrusted text and are shown as text only.
 */
export function CandidateList({
  candidates,
  imported = false,
}: Readonly<{
  candidates: readonly StaffImportCandidate[];
  /**
   * The row was imported. The import itself moved the item version and applied the update, so "changed
   * after the row was checked" and "would change nothing" no longer describe a future update.
   */
  imported?: boolean;
}>) {
  if (candidates.length === 0) return null;
  return (
    <section aria-labelledby="drawer-candidates-title" className="grid min-w-0 content-start gap-2">
      <h3 id="drawer-candidates-title" className={groupHeading}>
        Existing items that may match
      </h3>
      <ul className={cn(plainList, "gap-4")}>
        {candidates.map((candidate) => {
          const changed = !imported && candidate.itemVersionNow !== candidate.itemVersionSeen;
          return (
            <li key={candidate.catalogItemId} className={cn(listRow, "grid min-w-0 gap-2 p-4")}>
              <p className="m-0 flex flex-wrap items-center gap-2">
                <strong>{candidate.name}</strong> <ItemStatusBadge status={candidate.status} />
              </p>
              <p className={hintText}>
                {matchBasisLabels[candidate.matchBasis]}. {matchStrengthLabels[candidate.strength]}.
                {candidate.scoreBp === null ? "" : ` Similarity ${percent(candidate.scoreBp)}.`}
              </p>
              {changed ? (
                <Notice tone="warning" role="status">
                  This item was changed after the row was checked. The import will refuse to update
                  it.
                </Notice>
              ) : null}
              {imported ? null : candidate.unchanged ? (
                <p className="m-0">
                  <span aria-hidden="true">= </span>This update would change nothing.
                </p>
              ) : candidate.changes.length === 0 ? null : (
                <DataTable
                  banded
                  columns={changeColumns}
                  data={candidate.changes}
                  getRowId={(change) => change.fieldCode}
                  caption={`Changes an update would make to ${candidate.name}`}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
